import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowLeft, Play, Pause, Square, TrendingUp, TrendingDown, Zap, AlertTriangle, Settings, Save } from 'lucide-react';
import LiveTradingConfirmModal from '../components/ui/LiveTradingConfirmModal';
import FundingGuide from '../components/FundingGuide';

const BotControl = () => {
  const navigate = useNavigate();
  const { botId } = useParams();
  const [bot, setBot] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [allBots, setAllBots] = useState([]);
  const [editingRisk, setEditingRisk] = useState(false);
  const [riskParams, setRiskParams] = useState({
    allocation_percentage: 10,
    max_daily_loss: 5,
    stop_loss_percentage: 2
  });
  const [savingRisk, setSavingRisk] = useState(false);

  useEffect(() => {
    loadBotData();
    loadUser();
  }, [botId]);

  const loadUser = async () => {
    try {
      const response = await api.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user');
    }
  };

  const loadBotData = async () => {
    try {
      const [botsRes, tradesRes] = await Promise.all([api.getBots(), api.getTrades()]);
      setAllBots(botsRes.data);
      const currentBot = botsRes.data.find((b) => b.id === botId);
      if (!currentBot) {
        toast.error('Bot not found');
        navigate('/dashboard');
        return;
      }
      setBot(currentBot);
      setRiskParams({
        allocation_percentage: currentBot.allocation_percentage || 10,
        max_daily_loss: currentBot.max_daily_loss || 5,
        stop_loss_percentage: currentBot.stop_loss_percentage || 2
      });
      setTrades(tradesRes.data.filter((t) => t.bot_id === botId));
    } catch (error) {
      toast.error('Failed to load bot data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total allocation used by other bots
  const otherBotsAllocation = allBots
    .filter(b => b.id !== botId)
    .reduce((sum, b) => sum + (b.allocation_percentage || 0), 0);
  
  const maxAvailableAllocation = 100 - otherBotsAllocation;

  const handleSaveRiskParams = async () => {
    if (riskParams.allocation_percentage > maxAvailableAllocation) {
      toast.error(`Maximum available allocation is ${maxAvailableAllocation}%`);
      return;
    }

    setSavingRisk(true);
    try {
      await api.post(`/bots/${botId}/risk-params`, riskParams);
      toast.success('Risk parameters updated');
      setEditingRisk(false);
      loadBotData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update risk parameters');
    } finally {
      setSavingRisk(false);
    }
  };

  const handleStart = async () => {
    try {
      await api.startBot(botId);
      toast.success('Bot started');
      loadBotData();
    } catch (error) {
      toast.error('Failed to start bot');
    }
  };

  const handlePause = async () => {
    try {
      await api.pauseBot(botId);
      toast.success('Bot paused');
      loadBotData();
    } catch (error) {
      toast.error('Failed to pause bot');
    }
  };

  const handleStop = async () => {
    if (window.confirm('Are you sure you want to stop this bot permanently?')) {
      try {
        await api.stopBot(botId);
        toast.success('Bot stopped');
        navigate('/dashboard');
      } catch (error) {
        toast.error('Failed to stop bot');
      }
    }
  };

  const handleLiveTradingToggle = () => {
    if (bot.trading_mode === 'paper') {
      // Switching to live - show confirmation
      setShowLiveConfirm(true);
    } else {
      // Switching to paper - no confirmation needed
      switchTradingMode('paper');
    }
  };

  const switchTradingMode = async (mode) => {
    try {
      await api.post(`/bots/${botId}/mode`, { trading_mode: mode });
      toast.success(`Switched to ${mode} trading mode`);
      loadBotData();
    } catch (error) {
      toast.error('Failed to switch trading mode');
    }
  };

  const canUseLiveTrading = user && (user.subscription_tier === 'pro' || user.subscription_tier === 'elite' || user.subscription_tier === 'owner');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center">
        <div className="text-[#2F6BFF] text-lg">Loading...</div>
      </div>
    );
  }

  if (!bot) return null;

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
          <Button
            data-testid="bot-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Bot Header */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-semibold font-['Outfit'] mb-2 capitalize">
                {bot.strategy.replace('_', ' ')}
              </h1>
              <div className="flex items-center gap-3">
                <Badge
                  data-testid="bot-status-badge"
                  className={`${
                    bot.status === 'active'
                      ? 'bg-green-500'
                      : bot.status === 'paused'
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  } rounded-sm`}
                >
                  {bot.status}
                </Badge>
                <Badge className="bg-[#1E293B] rounded-sm">{bot.trading_mode}</Badge>
              </div>
            </div>
            <div className="flex gap-3">
              {bot.status !== 'active' && bot.status !== 'stopped' && (
                <Button
                  data-testid="bot-start-btn"
                  onClick={handleStart}
                  className="bg-green-500 hover:bg-green-600 rounded-sm"
                >
                  <Play className="w-4 h-4 mr-2" /> Start
                </Button>
              )}
              {bot.status === 'active' && (
                <Button
                  data-testid="bot-pause-btn"
                  onClick={handlePause}
                  className="bg-yellow-500 hover:bg-yellow-600 rounded-sm"
                >
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </Button>
              )}
              <Button
                data-testid="bot-stop-btn"
                onClick={handleStop}
                variant="destructive"
                className="rounded-sm"
              >
                <Square className="w-4 h-4 mr-2" /> Stop
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Bot Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Balance', value: `£${bot.paper_balance.toFixed(2)}`, color: '' },
            {
              label: 'P&L',
              value: `£${bot.paper_pnl.toFixed(2)}`,
              color: bot.paper_pnl >= 0 ? 'text-green-500' : 'text-red-500',
            },
            { label: 'Total Trades', value: bot.total_trades },
            { label: 'Win Rate', value: `${bot.win_rate.toFixed(1)}%` },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardContent className="pt-6">
                  <p className="text-sm text-[#94A3B8] mb-2">{stat.label}</p>
                  <p className={`text-2xl font-bold font-['JetBrains_Mono'] ${stat.color}`}>{stat.value}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Risk Parameters */}
        <Card className="bg-[#15171C] border-[#1E293B] mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Outfit'] flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#2F6BFF]" />
                Risk Parameters
              </CardTitle>
              {!editingRisk ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingRisk(true)}
                  className="border-[#1E293B]"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingRisk(false);
                      setRiskParams({
                        allocation_percentage: bot.allocation_percentage || 10,
                        max_daily_loss: bot.max_daily_loss || 5,
                        stop_loss_percentage: bot.stop_loss_percentage || 2
                      });
                    }}
                    className="border-[#1E293B]"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveRiskParams}
                    disabled={savingRisk}
                    className="bg-[#2F6BFF]"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {savingRisk ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!editingRisk ? (
              // Display mode
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <p className="text-sm text-[#94A3B8] mb-1">Allocation</p>
                  <p className="text-2xl font-bold text-[#2F6BFF]">{bot.allocation_percentage}%</p>
                  <p className="text-xs text-[#64748B] mt-1">of your total balance</p>
                </div>
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <p className="text-sm text-[#94A3B8] mb-1">Max Daily Loss</p>
                  <p className="text-2xl font-bold text-yellow-400">{bot.max_daily_loss}%</p>
                  <p className="text-xs text-[#64748B] mt-1">auto-pause trigger</p>
                </div>
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <p className="text-sm text-[#94A3B8] mb-1">Stop Loss</p>
                  <p className="text-2xl font-bold text-red-400">{bot.stop_loss_percentage}%</p>
                  <p className="text-xs text-[#64748B] mt-1">per trade</p>
                </div>
              </div>
            ) : (
              // Edit mode
              <div className="space-y-6">
                {/* Allocation Slider */}
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-[#F5F7FA]">Allocation Percentage</Label>
                      <p className="text-xs text-[#64748B]">How much of your balance this bot can use</p>
                    </div>
                    <span className="text-2xl font-bold text-[#2F6BFF]">{riskParams.allocation_percentage}%</span>
                  </div>
                  <Slider
                    value={[riskParams.allocation_percentage]}
                    onValueChange={([value]) => setRiskParams(prev => ({ ...prev, allocation_percentage: value }))}
                    min={5}
                    max={Math.min(50, maxAvailableAllocation)}
                    step={5}
                    className="my-4"
                  />
                  <div className="flex justify-between text-xs text-[#64748B]">
                    <span>5%</span>
                    <span className="text-[#94A3B8]">
                      Available: {maxAvailableAllocation}% (Other bots using {otherBotsAllocation}%)
                    </span>
                    <span>{Math.min(50, maxAvailableAllocation)}%</span>
                  </div>
                </div>

                {/* Max Daily Loss Slider */}
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-[#F5F7FA]">Max Daily Loss</Label>
                      <p className="text-xs text-[#64748B]">Bot pauses if daily loss exceeds this</p>
                    </div>
                    <span className="text-2xl font-bold text-yellow-400">{riskParams.max_daily_loss}%</span>
                  </div>
                  <Slider
                    value={[riskParams.max_daily_loss]}
                    onValueChange={([value]) => setRiskParams(prev => ({ ...prev, max_daily_loss: value }))}
                    min={1}
                    max={20}
                    step={1}
                    className="my-4"
                  />
                  <div className="flex justify-between text-xs text-[#64748B]">
                    <span>1% (Conservative)</span>
                    <span>20% (Aggressive)</span>
                  </div>
                </div>

                {/* Stop Loss Slider */}
                <div className="p-4 bg-[#0B0D12] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-[#F5F7FA]">Stop Loss Per Trade</Label>
                      <p className="text-xs text-[#64748B]">Maximum loss per individual trade</p>
                    </div>
                    <span className="text-2xl font-bold text-red-400">{riskParams.stop_loss_percentage}%</span>
                  </div>
                  <Slider
                    value={[riskParams.stop_loss_percentage]}
                    onValueChange={([value]) => setRiskParams(prev => ({ ...prev, stop_loss_percentage: value }))}
                    min={0.5}
                    max={10}
                    step={0.5}
                    className="my-4"
                  />
                  <div className="flex justify-between text-xs text-[#64748B]">
                    <span>0.5% (Tight)</span>
                    <span>10% (Wide)</span>
                  </div>
                </div>

                {/* Warning if allocation is high */}
                {riskParams.allocation_percentage > 25 && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-400">
                      High allocation ({riskParams.allocation_percentage}%) increases risk. Consider spreading across multiple bots.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trading Mode */}
        <Card className="bg-[#15171C] border-[#1E293B] mb-8">
          <CardHeader>
            <CardTitle className="font-['Outfit'] flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#2F6BFF]" />
              Trading Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-[#0B0D12] rounded-lg">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label htmlFor="live-trading" className="text-[#F5F7FA] font-medium">
                    Live Trading
                  </Label>
                  {bot.trading_mode === 'live' && (
                    <Badge className="bg-[#EF4444]/20 text-[#EF4444] text-xs">ACTIVE</Badge>
                  )}
                </div>
                <p className="text-sm text-[#94A3B8]">
                  {bot.trading_mode === 'live' 
                    ? 'Bot is trading with real funds on your connected exchange'
                    : 'Enable to trade with real funds (requires Pro/Elite subscription)'
                  }
                </p>
              </div>
              <Switch
                data-testid="live-trading-toggle"
                id="live-trading"
                checked={bot.trading_mode === 'live'}
                onCheckedChange={handleLiveTradingToggle}
                disabled={!canUseLiveTrading || bot.status === 'stopped'}
              />
            </div>

            {/* Warning for live mode */}
            {bot.trading_mode === 'live' && (
              <div className="mt-4 p-4 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-[#EF4444] font-medium">Live Trading Active</p>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Real orders are being placed on your exchange. Make sure your API keys have trading permissions only.
                  </p>
                </div>
              </div>
            )}

            {/* Upgrade prompt for non-eligible users */}
            {!canUseLiveTrading && (
              <div className="mt-4 p-4 bg-[#2F6BFF]/10 border border-[#2F6BFF]/30 rounded-lg">
                <p className="text-sm text-[#2F6BFF]">
                  Upgrade to Pro or Elite to enable live trading with real funds.
                </p>
              </div>
            )}

            {/* Transition Wizard button for paper mode */}
            {bot.trading_mode === 'paper' && canUseLiveTrading && (
              <div className="mt-4 flex justify-end">
                <Button
                  data-testid="transition-wizard-btn"
                  onClick={() => navigate(`/transition/${bot.id}`)}
                  className="bg-gradient-to-r from-[#2F6BFF] to-purple-500 hover:opacity-90"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Paper-to-Live Wizard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Funding Guide - Show when in paper mode */}
        {bot.trading_mode === 'paper' && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FundingGuide variant="compact" />
          </motion.div>
        )}

        {/* Trade History */}
        <Card className="bg-[#15171C] border-[#1E293B]">
          <CardHeader>
            <CardTitle className="font-['Outfit']">Trade History</CardTitle>
          </CardHeader>
          <CardContent>
            {trades.length === 0 ? (
              <p className="text-[#94A3B8] text-center py-8">No trades executed yet</p>
            ) : (
              <div className="space-y-3">
                {trades.map((trade) => (
                  <div
                    key={trade.id}
                    data-testid={`trade-${trade.id}`}
                    className="p-4 bg-[#0B0D12] border border-[#1E293B] rounded-sm flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-2 rounded-sm ${
                          trade.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {trade.side === 'buy' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="font-semibold">{trade.symbol}</p>
                        <p className="text-sm text-[#94A3B8]">
                          {new Date(trade.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['JetBrains_Mono'] font-semibold">
                        {trade.quantity.toFixed(6)} @ £{trade.price.toFixed(2)}
                      </p>
                      <p
                        className={`text-sm font-bold font-['JetBrains_Mono'] ${
                          trade.pnl >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        {trade.pnl >= 0 ? '+' : ''}£{trade.pnl.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Trading Confirmation Modal */}
      <LiveTradingConfirmModal
        isOpen={showLiveConfirm}
        onClose={() => setShowLiveConfirm(false)}
        onConfirm={() => switchTradingMode('live')}
        botName={`Bot #${bot.id.slice(0, 8)}`}
        strategy={bot.strategy.replace('_', ' ')}
      />
    </div>
  );
};

export default BotControl;